import { useCallback, useEffect, useRef, useState } from 'react';
import { GMBJT_PLAYLIST_URL, GMBJT_MUSIC_PAGE_URL, GMBJT_SONGS } from './gmbjt-songs';
import type { GMBJTSong } from './gmbjt-songs';

const VOLUME_KEY = 'chess960x32_music_volume';
const MUSIC_ENABLED_KEY = 'chess960x32_music_enabled';

function getStoredVolume(): number {
  try {
    const v = localStorage.getItem(VOLUME_KEY);
    return v !== null ? parseFloat(v) : 0.5;
  } catch {
    return 0.5;
  }
}

function storeVolume(vol: number): void {
  try { localStorage.setItem(VOLUME_KEY, String(vol)); } catch { /* ignore */ }
}

function getStoredMusicPref(): boolean {
  try { return localStorage.getItem(MUSIC_ENABLED_KEY) === 'true'; } catch { return false; }
}

function storeMusicPref(enabled: boolean): void {
  try { localStorage.setItem(MUSIC_ENABLED_KEY, String(enabled)); } catch { /* ignore */ }
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds)) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface BackgroundMusicPlayerProps {
  visible?: boolean;
}

export const BackgroundMusicPlayer: React.FC<BackgroundMusicPlayerProps> = ({ visible = true }) => {
  const [expanded, setExpanded] = useState(false);
  const [enabled, setEnabled] = useState(getStoredMusicPref);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(getStoredVolume);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Persist volume
  useEffect(() => {
    storeVolume(volume);
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Persist enabled state
  useEffect(() => {
    storeMusicPref(enabled);
  }, [enabled]);

  const currentSong: GMBJTSong | null = currentIndex !== null ? GMBJT_SONGS[currentIndex] ?? null : null;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const playSongRef = useRef<(index: number) => void>(() => {});

  const playSong = useCallback((index: number) => {
    const song = GMBJT_SONGS[index];
    if (!song) return;

    stopAudio();

    const audio = new Audio(song.url);
    audio.volume = volume;
    audio.crossOrigin = 'anonymous';

    audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
    audio.addEventListener('ended', () => {
      const next = (index + 1) % GMBJT_SONGS.length;
      playSongRef.current(next);
    });

    audio.play().then(() => {
      setIsPlaying(true);
    }).catch(() => {
      setIsPlaying(false);
    });

    audioRef.current = audio;
    setCurrentIndex(index);
  }, [stopAudio, volume]);

  useEffect(() => {
    playSongRef.current = playSong;
  }, [playSong]);

  const togglePlayPause = useCallback(() => {
    if (!audioRef.current) {
      playSong(0);
      return;
    }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying, playSong]);

  const nextSong = useCallback(() => {
    const next = currentIndex !== null ? (currentIndex + 1) % GMBJT_SONGS.length : 0;
    playSong(next);
  }, [currentIndex, playSong]);

  const prevSong = useCallback(() => {
    const prev = currentIndex !== null
      ? (currentIndex - 1 + GMBJT_SONGS.length) % GMBJT_SONGS.length
      : 0;
    playSong(prev);
  }, [currentIndex, playSong]);

  const handleEnableToggle = useCallback(() => {
    setEnabled((prev) => {
      if (prev) {
        stopAudio();
        setCurrentIndex(null);
        setExpanded(false);
      }
      return !prev;
    });
  }, [stopAudio]);

  // Cleanup on unmount
  useEffect(() => () => stopAudio(), [stopAudio]);

  if (!visible) return null;

  // Not enabled: show minimal toggle
  if (!enabled) {
    return (
      <button
        onClick={handleEnableToggle}
        title="Play background music (Gambit Bonnevoie chess playlist)"
        aria-label="Enable background music"
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-300 transition-all duration-200 hover:border-slate-500 hover:bg-slate-700/80"
      >
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
        <span className="hidden sm:inline">Music</span>
      </button>
    );
  }

  return (
    <div className="w-full rounded-xl border border-cyan-400/15 bg-gradient-to-b from-[#1a1a2e] to-[#0f0f23] text-slate-200 shadow-lg overflow-hidden">
      {/* Collapsed bar */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-white/[0.03]"
        aria-label={expanded ? 'Collapse music player' : 'Expand music player'}
      >
        <span className="text-base text-rose-500">♫</span>
        <span className="text-sm font-semibold tracking-wide text-slate-200">GMBJT Music</span>
        <span className="flex-1 truncate px-2 text-center text-xs text-slate-400">
          {currentSong ? (
            <>
              {currentSong.title}
              <span className="ml-1">{isPlaying ? '▶' : '⏸'}</span>
            </>
          ) : (
            'Tap a song to play'
          )}
        </span>
        <span className="text-xs text-slate-500">{expanded ? '▲' : '▼'}</span>
        <button
          onClick={(e) => { e.stopPropagation(); handleEnableToggle(); }}
          className="ml-1 rounded p-1 text-xs text-slate-400 hover:text-rose-400 transition-colors"
          title="Disable music"
          aria-label="Disable music"
        >
          ✕
        </button>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="flex flex-col gap-3 border-t border-white/[0.06] px-4 py-3">
          {/* Now playing */}
          <div className="text-center">
            <div className="text-sm font-semibold text-white">
              {currentSong ? currentSong.title : 'GMBJT — Gambit Bonnevoie Chess Club Songs'}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {currentSong ? currentSong.author : '50 chess songs created with AI'}
            </div>
          </div>

          {/* Seek bar */}
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-slate-400 w-10 text-right">{formatTime(currentTime)}</span>
            <input
              type="range"
              min={0}
              max={100}
              step={0.1}
              value={progressPercent}
              onChange={(e) => {
                if (audioRef.current) {
                  audioRef.current.currentTime = (parseFloat(e.target.value) / 100) * duration;
                }
              }}
              className="flex-1 h-1 appearance-none bg-slate-700 rounded-full outline-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-rose-500 [&::-webkit-slider-thumb]:cursor-pointer"
              aria-label="Seek position"
            />
            <span className="font-mono text-xs text-slate-400 w-10">{formatTime(duration)}</span>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-3">
            <button onClick={prevSong} className="rounded p-1.5 text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors" title="Previous" aria-label="Previous song">⏮</button>
            <button
              onClick={togglePlayPause}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-500 text-white hover:bg-rose-400 transition-colors"
              title="Play/Pause"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button onClick={nextSong} className="rounded p-1.5 text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors" title="Next" aria-label="Next song">⏭</button>
          </div>

          {/* Volume */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">🔊</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-24 h-1 appearance-none bg-slate-700 rounded-full outline-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-slate-400 [&::-webkit-slider-thumb]:cursor-pointer"
              aria-label="Volume"
            />
          </div>

          {/* Song list */}
          <div className="max-h-48 overflow-y-auto rounded-lg bg-black/[0.15] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-slate-700">
            {GMBJT_SONGS.map((song, i) => (
              <button
                key={song.id}
                onClick={() => playSong(i)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors
                  ${i === currentIndex ? 'bg-rose-500/[0.12] text-white' : 'text-slate-400 hover:bg-white/[0.03] hover:text-slate-200'}
                `}
                aria-label={`Play ${song.title}`}
              >
                <span className="w-6 text-right text-slate-600">{i + 1}.</span>
                <span className="flex-1 truncate">{song.title}</span>
                {i === currentIndex && isPlaying && <span className="text-rose-500 animate-pulse">♫</span>}
              </button>
            ))}
          </div>

          {/* Footer links */}
          <div className="flex justify-center gap-4 border-t border-white/[0.06] pt-3 text-xs">
            <a href={GMBJT_PLAYLIST_URL} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-300 transition-colors">
              Full Playlist on Suno ↗
            </a>
            <a href={GMBJT_MUSIC_PAGE_URL} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-300 transition-colors">
              About GMBJT Music ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
